import { FeatureFlag } from "@backend-types";
import { MenuItem, TextField, Chip, Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../../../styles/Theme";

type FeatureFlagSelectProps = {
  featureIds: string[];
  onChange: (id: string[]) => void;
  options: FeatureFlag[];
  disabled: boolean;
};
const useStyle = makeStyles((theme) => ({
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
}));
// Generate the feature flag label with dev data (id, branch, status) if in staging or dev
// Otherwise display the friendly name and status
function makeFeatureFlagName(featureFlag: FeatureFlag): string {
  const dev = process.env.NODE_ENV === "production" ? false : true;
  if (dev) {
    return `${featureFlag.id} - ${featureFlag.branch} - ${
      featureFlag.released ? "Released" : "Unreleased"
    }`;
  } else {
    return `${featureFlag.name} - ${featureFlag.released ? "Released" : "Unreleased"}`;
  }
}

export function FeatureFlagSelect(props: FeatureFlagSelectProps) {
  const classes = useStyle();
  const emptyAndDisabled = !props.featureIds && props.disabled;

  const handleChange = (newFeatureId: string) => {
    if (props.featureIds.indexOf(newFeatureId) === -1) {
      props.onChange([...props.featureIds, newFeatureId]);
    }
  };

  const handleDelete = (deleteFeatureId: string) => {
    const filteredFeatureIds = props.featureIds.filter((featureId) => {
      return featureId !== deleteFeatureId;
    });
    props.onChange(filteredFeatureIds);
  };

  const getFeatureLabel = (featureId: string) => {
    const match = props.options.find((feature) => feature.id === Number(featureId));
    return match ? makeFeatureFlagName(match) : featureId;
  };

  return (
    <>
      <TextField
        className={classes.inputText}
        value={
          props.featureIds.length ? props.featureIds[props.featureIds.length - 1] : ""
        }
        onChange={(e) => handleChange(`${e.target.value}`)}
        disabled={props.disabled}
        label="Feature Flag"
        required={false}
        InputLabelProps={{
          shrink: emptyAndDisabled || undefined,
        }}
        SelectProps={{
          displayEmpty: true,
          // Renders 'N/A' without actually setting the value
          renderValue: emptyAndDisabled ? () => "N/A" : undefined,
        }}
        fullWidth
        select>
        {props.options.map((featureFlag) => (
          <MenuItem key={featureFlag.id} value={featureFlag.id}>
            {makeFeatureFlagName(featureFlag)}
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, marginTop: "1rem" }}>
        {props.options.length > 0 &&
          props.featureIds.map((featureId) => (
            <Chip
              key={featureId}
              label={getFeatureLabel(featureId)}
              onDelete={() => handleDelete(featureId)}
            />
          ))}
      </Box>
    </>
  );
}
